/** Pure selection and keyboard logic for the Select component. */

export interface SelectOption {
  value: string;
  label: string;
}

export type SelectKeyAction =
  | { type: 'open'; index: number }
  | { type: 'move'; index: number }
  | { type: 'choose'; index: number }
  | { type: 'close' };

/** The label shown on the trigger for the current value. */
export function selectedOptionLabel(options: SelectOption[], value: string): string {
  return options.find((option) => option.value === value)?.label ?? '';
}

/** Index of the next option, after `fromIndex`, whose label starts with `query`. */
export function typeaheadIndex(options: SelectOption[], query: string, fromIndex: number): number {
  const needle = query.toLowerCase();
  if (!needle || options.length === 0) return -1;
  for (let step = 1; step <= options.length; step += 1) {
    const index = (fromIndex + step) % options.length;
    if (options[index]!.label.toLowerCase().startsWith(needle)) return index;
  }
  return -1;
}

/** What a key does, for a list of `count` options. Null means the key is not handled. */
export function selectKeyAction(
  key: string,
  state: { open: boolean; activeIndex: number; selectedIndex: number; count: number },
): SelectKeyAction | null {
  const last = state.count - 1;
  if (state.count === 0) return null;
  if (!state.open) {
    if (key === 'ArrowDown' || key === 'ArrowUp' || key === 'Enter' || key === ' ') {
      return { type: 'open', index: Math.max(0, state.selectedIndex) };
    }
    if (key === 'Home') return { type: 'open', index: 0 };
    if (key === 'End') return { type: 'open', index: last };
    return null;
  }
  switch (key) {
    case 'ArrowDown':
      return { type: 'move', index: Math.min(last, state.activeIndex + 1) };
    case 'ArrowUp':
      return { type: 'move', index: Math.max(0, state.activeIndex - 1) };
    case 'Home':
      return { type: 'move', index: 0 };
    case 'End':
      return { type: 'move', index: last };
    case 'Enter':
    case ' ':
      return { type: 'choose', index: state.activeIndex };
    case 'Escape':
    case 'Tab':
      return { type: 'close' };
    default:
      return null;
  }
}
